import { createGlobalStyle } from "styled-components";
import { color, font, media, motion, size } from "./theme";

/**
 * Global stylesheet.
 *
 * Everything here is deliberately dark-first: the page has one theme and it is
 * the inside of a condemned building. Tokens are mirrored onto :root as CSS
 * custom properties so plain CSS (and any third-party markup) can use them.
 */
export const Styles = createGlobalStyle`
    /* NOTE: the @font-face rules deliberately live in public/index.html, not
       here. createGlobalStyle injects its CSS from JavaScript once React
       mounts, so a font declared here can't be fetched until the bundle has
       downloaded and parsed — the browser doesn't know it exists. That's what
       made the type visibly change on every load. Declaring them in the HTML
       head lets the fetch start during initial parse instead.

       If you add another webfont, add it there too. */

    :root {
        --color-void: ${color.void};
        --color-abyss: ${color.abyss};
        --color-crypt: ${color.crypt};
        --color-slab: ${color.slab};
        --color-ash: ${color.ash};
        --color-smoke: ${color.smoke};
        --color-bone: ${color.bone};
        --color-pale: ${color.pale};
        --color-blood: ${color.blood};
        --color-blood-bright: ${color.bloodBright};
        --color-ember: ${color.ember};

        --font-display: ${font.display};
        --font-label: ${font.label};
        --font-body: ${font.body};

        --ease: ${motion.ease};

        color-scheme: dark;
    }

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    html {
        scroll-behavior: smooth;
        /* Keeps anchored sections clear of the fixed header. */
        scroll-padding-top: 90px;
        -webkit-text-size-adjust: 100%;
    }

    ${media.motionReduce} {
        html {
            scroll-behavior: auto;
        }
    }

    body,
    html {
        margin: 0;
        padding: 0;
        border: 0;
        outline: 0;
        font-family: ${font.body};
        background-color: ${color.void};
        color: ${color.bone};
        overflow-x: hidden;
        min-height: 100%;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    body {
        /* Two cold pools of light bleeding in from the edges. */
        background-image:
            radial-gradient(
                ellipse 80% 55% at 50% -10%,
                rgba(142, 22, 22, 0.13),
                transparent 60%
            ),
            radial-gradient(
                ellipse 60% 50% at 100% 100%,
                rgba(30, 34, 48, 0.35),
                transparent 65%
            );
        background-attachment: fixed;
    }

    h1, h2, h3, h4, h5, h6 {
        font-family: ${font.display};
        font-weight: 600;
        color: ${color.pale};
        letter-spacing: 0.02em;
        line-height: 1.12;
        margin: 0 0 0.6em;
        text-wrap: balance;
    }

    h1 { font-size: ${size.h1}; }
    h2 { font-size: ${size.h2}; }
    h3 { font-size: ${size.h3}; }
    h4 { font-size: ${size.h4}; }

    p {
        color: ${color.bone};
        font-size: ${size.body};
        line-height: 1.68;
        margin: 0 0 1.15em;
    }

    a {
        color: ${color.ember};
        text-decoration: none;
        transition: color ${motion.fast} ${motion.easeOut};
    }

    a:hover {
        color: ${color.pale};
    }

    img {
        max-width: 100%;
        display: block;
    }

    button {
        font-family: inherit;
    }

    ul, ol {
        color: ${color.bone};
        line-height: 1.68;
    }

    hr {
        border: 0;
        height: 1px;
        background: linear-gradient(
            90deg,
            transparent,
            ${color.ash},
            transparent
        );
        margin: 2.5rem 0;
    }

    ::selection {
        background: ${color.blood};
        color: ${color.white};
    }

    /* --- Focus -------------------------------------------------------- */
    /* Visible only for keyboard users, but always visible for them. */
    :focus {
        outline: none;
    }

    :focus-visible {
        outline: 2px solid ${color.ember};
        outline-offset: 3px;
        border-radius: 2px;
    }

    /* --- Scrollbar ---------------------------------------------------- */
    * {
        scrollbar-width: thin;
        scrollbar-color: ${color.ash} ${color.void};
    }

    ::-webkit-scrollbar {
        width: 10px;
        height: 10px;
    }

    ::-webkit-scrollbar-track {
        background: ${color.void};
    }

    ::-webkit-scrollbar-thumb {
        background: ${color.ash};
        border: 2px solid ${color.void};
        border-radius: 999px;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: ${color.blood};
    }

    /* --- Utilities ---------------------------------------------------- */
    .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
        border: 0;
    }

    /* Set while a modal or the mobile drawer is open. */
    body.is-locked {
        overflow: hidden;
    }

    /* --- Keyframes ---------------------------------------------------- */
    @keyframes ghost-flicker {
        0%, 100%   { opacity: 1; }
        41%        { opacity: 1; }
        42%        { opacity: 0.35; }
        43%        { opacity: 1; }
        45%        { opacity: 0.55; }
        46%        { opacity: 1; }
        78%        { opacity: 1; }
        79%        { opacity: 0.28; }
        80%        { opacity: 1; }
    }

    @keyframes ghost-drift {
        0%   { transform: translate3d(0, 0, 0) scale(1); }
        50%  { transform: translate3d(4%, -3%, 0) scale(1.12); }
        100% { transform: translate3d(0, 0, 0) scale(1); }
    }

    @keyframes ghost-grain {
        0%   { transform: translate(0, 0); }
        10%  { transform: translate(-5%, -10%); }
        20%  { transform: translate(-15%, 5%); }
        30%  { transform: translate(7%, -25%); }
        40%  { transform: translate(-5%, 25%); }
        50%  { transform: translate(-15%, 10%); }
        60%  { transform: translate(15%, 0); }
        70%  { transform: translate(0, 15%); }
        80%  { transform: translate(3%, 35%); }
        90%  { transform: translate(-10%, 10%); }
        100% { transform: translate(0, 0); }
    }

    @keyframes ghost-pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(184, 29, 29, 0.42); }
        70%      { box-shadow: 0 0 0 14px rgba(184, 29, 29, 0); }
    }

    @keyframes ghost-bob {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(8px); }
    }

    /* --- Reduced motion ----------------------------------------------- */
    /* One switch. Anything that moves, stops. */
    ${media.motionReduce} {
        *,
        *::before,
        *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
`;
